/**
 * Convert pasted HTML to Markdown (PLAN.md §5.10).
 *
 * People paste from Outlook, Word and web pages constantly. Without this, a pasted reply arrives
 * as either a wall of unformatted text or a soup of `<span style="mso-...">` — and since the
 * store format is Markdown, the formatting is lost either way.
 *
 * Parsed with `DOMParser` into an inert document rather than by regex: the input is untrusted
 * clipboard content, and a regex-based converter that mishandles nesting is exactly how markup
 * leaks through. `text/html` documents from `DOMParser` do not execute scripts or load
 * subresources, and the output here is plain text regardless.
 *
 * Deliberately partial: it handles the constructs that survive a paste and drops the rest to
 * their text content. A converter that tried to be exhaustive would produce worse Markdown for
 * the common case.
 */

const BLOCK_TAGS = new Set(['P', 'DIV', 'SECTION', 'ARTICLE', 'BLOCKQUOTE', 'PRE', 'UL', 'OL', 'LI', 'TABLE', 'TR', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BR', 'HR']);

/** Escape Markdown control characters so pasted literal text doesn't become formatting. */
function escapeText(text: string): string {
  return text.replace(/([\\`*_[\]#])/g, '\\$1');
}

function convertNode(node: Node, depth: number, listIndex: number | null): string {
  if (node.nodeType === Node.TEXT_NODE) {
    // Collapse the whitespace HTML would have collapsed anyway.
    return escapeText((node.textContent ?? '').replace(/\s+/g, ' '));
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const element = node as HTMLElement;
  const children = (index: number | null = null) =>
    Array.from(element.childNodes)
      .map((child) => convertNode(child, depth + 1, index))
      .join('');

  switch (element.tagName) {
    case 'BR':
      return '\n';
    case 'HR':
      return '\n---\n';
    case 'STRONG':
    case 'B': {
      const inner = children().trim();
      return inner ? `**${inner}**` : '';
    }
    case 'EM':
    case 'I': {
      const inner = children().trim();
      return inner ? `*${inner}*` : '';
    }
    case 'CODE':
      // Inside <pre> the parent handles fencing; standalone <code> is inline.
      return element.parentElement?.tagName === 'PRE'
        ? (element.textContent ?? '')
        : `\`${element.textContent ?? ''}\``;
    case 'PRE':
      return `\n\`\`\`\n${element.textContent ?? ''}\n\`\`\`\n`;
    case 'A': {
      const href = element.getAttribute('href') ?? '';
      const label = children().trim() || href;
      // Only http(s) and mailto survive. A pasted `javascript:` link would be neutered by the
      // backend sanitiser too, but stripping it here means it never reaches the stored text.
      return /^(?:https?:|mailto:)/i.test(href) ? `[${label}](${href})` : label;
    }
    case 'H1':
    case 'H2':
    case 'H3':
    case 'H4':
    case 'H5':
    case 'H6': {
      const level = Number(element.tagName[1]);
      return `\n${'#'.repeat(level)} ${children().trim()}\n`;
    }
    case 'BLOCKQUOTE':
      return `\n${children()
        .trim()
        .split('\n')
        .map((line) => `> ${line}`)
        .join('\n')}\n`;
    case 'UL':
    case 'OL': {
      const ordered = element.tagName === 'OL';
      const items = Array.from(element.children).filter((child) => child.tagName === 'LI');
      return (
        '\n' +
        items
          .map((item, index) => {
            const marker = ordered ? `${index + 1}.` : '-';
            const indent = '  '.repeat(Math.max(0, depth - 1));
            const body = Array.from(item.childNodes)
              .map((child) => convertNode(child, depth + 1, null))
              .join('')
              .trim();
            return `${indent}${marker} ${body}`;
          })
          .join('\n') +
        '\n'
      );
    }
    case 'LI':
      // Reached only for a stray <li>; its parent normally handles it.
      return `- ${children().trim()}\n`;
    case 'TR':
      return `| ${Array.from(element.children)
        .map((cell) => convertNode(cell, depth + 1, null).trim())
        .join(' | ')} |\n`;
    case 'TABLE': {
      const rows = Array.from(element.querySelectorAll('tr'));
      if (rows.length === 0) return '';
      const rendered = rows.map((row) => convertNode(row, depth + 1, null));
      const columnCount = rows[0]?.children.length ?? 0;
      // Markdown tables need the separator row directly under the header.
      const separator = `|${' --- |'.repeat(columnCount)}\n`;
      return `\n${rendered[0] ?? ''}${separator}${rendered.slice(1).join('')}\n`;
    }
    case 'SCRIPT':
    case 'STYLE':
    case 'HEAD':
      // Never carry these through, not even as text.
      return '';
    default: {
      const inner = children(listIndex);
      return BLOCK_TAGS.has(element.tagName) ? `\n${inner}\n` : inner;
    }
  }
}

export function htmlToMarkdown(html: string): string {
  const document_ = new DOMParser().parseFromString(html, 'text/html');
  return convertNode(document_.body, 0, null)
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
